import Vue from 'vue';
import VueRouter from 'vue-router';
import storage from 'store';
import { Message } from 'element-ui';

import PreLogin from '@/views/PreLogin.vue';
import PostLogin from '@/views/PostLogin.vue';
import store from '@/store';
import { usersApi } from '@/api';
import auth from '@/services/auth';

import users from './users';
import roles from './roles';
import operations from './operations';
import receivedSamples from './receivedSamples';
import workPlanning from './workPlanning';
import quarantine from './quarantine';
import insertAnalysesData from './insertAnalysesData';
import sampleDetails from './sampleDetails';
import analysesStatusManagement from './analysesStatusManagement';
import analysesSearch from './analysesSearch';
import printAnalyses from './printAnalyses';
import groupings from './groupings';
import signatureConfig from './signatureConfig';
import changeLog from './changeLog';
import thermostats from './thermostats';
import offers from './offers';
import locationsManagement from './locationsManagement';
import tasks from './tasks';
import systemConfig from './systemConfig';
import signatureRecipients from './signatureRecipients';
import signDocuments from './signDocuments';
import signatureProfiles from './signatureProfiles';
import approvalCustomerChanges from './approvalCustomerChanges';
import sectorsAssociation from './sectorsAssociation';
import defaultLocations from './defaultLocations';
import customersLocationsManagement from './customersLocationsManagement';
import customerSampleAnalyses from './customerSampleAnalyses';
import familyTypes from './familyTypes';
import familyCategories from './familyCategories';
import uploadDocument from './uploadDocument';
import symbolSearch from './symbolSearch';
import analysesThermostatsSearch from './analysesThermostatsSearch';
import stability from './stability';
import advancedRevision from './advancedRevision';
import monitoringDashboard from './dashboard';

import acl from '../services/acl';
import i18n from '../i18n';

Vue.use(VueRouter);

const routes = [
  {
    path: '/admin',
    name: 'postLogin',
    component: PostLogin,
    children: [
      ...monitoringDashboard,
      ...users,
      ...roles,
      ...operations,
      ...receivedSamples,
      ...workPlanning,
      ...quarantine,
      ...insertAnalysesData,
      ...sampleDetails,
      ...analysesStatusManagement,
      ...analysesSearch,
      ...printAnalyses,
      ...groupings,
      ...signatureConfig,
      ...changeLog,
      ...thermostats,
      ...offers,
      ...locationsManagement,
      ...tasks,
      ...systemConfig,
      ...signatureRecipients,
      ...signDocuments,
      ...signatureProfiles,
      ...approvalCustomerChanges,
      ...sectorsAssociation,
      ...defaultLocations,
      ...customersLocationsManagement,
      ...customerSampleAnalyses,
      ...familyTypes,
      ...familyCategories,
      ...uploadDocument,
      ...symbolSearch,
      ...analysesThermostatsSearch,
      ...stability,
      ...advancedRevision,
    ],
  },
  {
    path: '/',
    name: 'preLogin',
    component: PreLogin,
    redirect: '/login',
    children: [
      {
        path: '/login',
        name: 'Login',
        component: () =>
          import(/* webpackChunkName: "login" */ '../views/Login.vue'),
        beforeEnter: (to, from, next) => {
          const authToken = storage.get('authToken');
          if (store.getters.loggedUser && authToken) {
            next({ name: 'postLogin' });
          } else {
            next();
          }
        },
      },
    ],
  },
];

const router = new VueRouter({
  routes,
});

router.beforeEach(async (to, from, next) => {
  if (
    !!to.matched.find((route) => route.name === 'postLogin') &&
    !store.getters.loggedUser
  ) {
    try {
      const response = await usersApi.getInfo();
      store.dispatch('setLoggedUser', response.data);
      if (store.getters.loggedUser && store.getters.loggedUser.metadata) {
        const { metadata } = store.getters.loggedUser;
        Object.keys(metadata).forEach((tableKey) => {
          if (!store.getters.tablesStatus[tableKey]) {
            return;
          }
          if (metadata[tableKey]?.enabledColumns?.length) {
            store.dispatch('updateTableColumnsConfig', {
              tableKey,
              config: metadata[tableKey].enabledColumns,
            });
          }
          if (metadata[tableKey]?.stickyColumns?.length) {
            store.dispatch('updateTableStickyColumns', {
              tableKey,
              config: metadata[tableKey].stickyColumns,
            });
          }
        });
      }
      if (!store.getters.loggedUser) {
        next({ name: 'Login' });
        return;
      }
    } catch (err) {
      if (err.response.status === 401 || !auth.authTokenExists()) {
        auth.logout();
        store.dispatch('setLoggedUser', null);
        next({ name: 'Login' });
        return;
      }
    }
  }

  if (to.meta.tabbable) {
    const { name, path, query, meta, params } = to;
    const formattedName = meta.dynamicTabKey
      ? `${name}${params[meta.dynamicTabKey]}`
      : name;
    const existingTab = store.getters.tabs.find(
      (t) => t.name === formattedName
    );
    store.dispatch('addTab', {
      name: formattedName,
      routeName: name,
      path,
      query:
        Object.keys(query).length === 0 && existingTab
          ? { ...existingTab.query }
          : query,
      params,
      meta: existingTab ? { ...meta, ...existingTab.meta } : meta,
    });
  }

  if (acl.userCanUse((to.meta || {}).routePermissions)) next();
  else {
    next({ name: 'Login' });
    Message({
      showClose: true,
      type: 'error',
      message: i18n.t('global.noPermissonRoute'),
    });
  }
});

export default router;
